import { DialogComponent } from '@theme/dialog';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent<Refs>}
 */
class HeaderDrawer extends DialogComponent {
  requiredRefs = ['dialog'];
  #previousScrollY = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#setupAnimatedElementListeners();
    
    // Handle escape key at document level
    document.addEventListener('keydown', this.#handleKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.#handleKeyDown);
  }

  /**
   * Handle escape key to close dialog
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    if (!this.refs.dialog.open) return;
    
    event.preventDefault();
    event.stopPropagation();
    this.closeDialog();
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.dialog.open === true;
  }

  /**
   * Override showDialog to use show() instead of showModal() so header remains clickable
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // If body is already fixed (another drawer was open), preserve scroll position from body.style.top
    // Otherwise, use current window scroll position
    let scrollY;
    if (document.body.style.position === 'fixed' && document.body.style.top) {
      scrollY = -parseInt(document.body.style.top) || 0;
    } else {
      scrollY = window.scrollY;
    }
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      // Use show() instead of showModal() so header can remain clickable above the dialog
      dialog.show();
      this.dispatchEvent(new CustomEvent('dialog:open'));

      // Attach click handler directly to backdrop
      const backdrop = this.querySelector('.menu-drawer__backdrop');
      if (backdrop) {
        backdrop.addEventListener('click', this.#handleBackdropClick);
      }

      // Prevent initial accordion animations
      this.preventInitialAccordionAnimations(dialog);
      
      // Add menu-open class for animations
      requestAnimationFrame(() => {
        dialog.classList.add('menu-open');
        setTimeout(() => {
          trapFocus(dialog);
        }, 0);
      });
    });
  }

  /**
   * Handle clicks to close dialog when clicking on backdrop
   */
  #handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) {
      this.closeDialog();
    }
  };

  /**
   * Override closeDialog to restore scroll position and handle animations
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    // Check if another drawer is opening (to preserve scroll position during transitions)
    // Check if drawer coordinator is transitioning OR if another drawer is actually open
    const coordinator = window.drawerCoordinator;
    const isAnotherDrawerOpening = document.body.hasAttribute('data-drawer-transitioning') || 
      (coordinator && coordinator.hasAnyDrawerOpen());

    // Remove event listeners
    const backdrop = this.querySelector('.menu-drawer__backdrop');
    if (backdrop) {
      backdrop.removeEventListener('click', this.#handleBackdropClick);
    }

    dialog.classList.add('dialog-closing');
    dialog.classList.remove('menu-open');

    // Wait for transition to complete (matching search drawer behavior)
    const transitionDuration = getComputedStyle(dialog).getPropertyValue('--animation-speed') || '0.3s';
    const durationMs = parseFloat(transitionDuration) * (transitionDuration.includes('ms') ? 1 : 1000);

    await new Promise((/** @type {(value?: unknown) => void} */ resolve) => {
      const handleTransitionEnd = (/** @type {TransitionEvent} */ event) => {
        if (event.target === dialog && event.propertyName === 'transform') {
          dialog.removeEventListener('transitionend', handleTransitionEnd);
          resolve();
        }
      };

      dialog.addEventListener('transitionend', handleTransitionEnd);

      setTimeout(() => {
        dialog.removeEventListener('transitionend', handleTransitionEnd);
        resolve();
      }, durationMs + 50);
    });

    // Only restore scroll position if no other drawer is opening
    if (!isAnotherDrawerOpening) {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    } else {
      // Another drawer is opening - don't remove fixed positioning
      // Keep body fixed so the scroll position is preserved in body.style.top
      // The new drawer will read from body.style.top when it opens
    }

    dialog.close();
    dialog.classList.remove('dialog-closing');
    removeTrapFocus();
    
    // Reset any open submenu details
    const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
    openDetails.forEach(reset);

    this.dispatchEvent(new CustomEvent('dialog:close'));
  };

  /**
   * Open the menu drawer
   */
  open(event) {
    // For submenu navigation in 3-level menus
    if (event && event.target) {
      const details = event.target.closest('details');
      if (details && details !== this.refs.dialog) {
        const summary = details.querySelector('summary');
        if (summary) {
          summary.setAttribute('aria-expanded', 'true');
          details.classList.add('menu-open');
          return;
        }
      }
    }
    
    // For main drawer
    this.showDialog();
  }

  /**
   * Close the menu drawer
   */
  close() {
    this.closeDialog();
  }

  /**
   * Toggle the menu drawer
   */
  toggle() {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  }

  /**
   * Go back in submenu navigation (for 3-level menus)
   */
  back(event) {
    if (!(event?.target instanceof Element)) return;
    
    const details = event.target.closest('details');
    if (details && details !== this.refs.dialog) {
      const summary = details.querySelector('summary');
      if (summary) {
        summary.setAttribute('aria-expanded', 'false');
        details.classList.remove('menu-open');

        onAnimationEnd(details, () => {
          reset(details);
          setTimeout(() => {
            trapFocus(this.refs.dialog);
          }, 0);
        });
      }
    }
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDialogElement} dialog - The dialog element containing the accordions
   */
  preventInitialAccordionAnimations(dialog) {
    const content = dialog.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
